import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
// --- Helper Functions ---
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};
const STORAGE_KEY = 'aiChatSessions';
const AIChatContext = createContext(undefined);
// --- Provider Component ---
export const AIChatProvider = ({ children }) => {
    const [sessions, setSessions] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isGeneratingInBackground, setIsGeneratingInBackground] = useState(false);
    // Load sessions from localStorage on initial render
    useEffect(() => {
        try {
            const savedSessions = localStorage.getItem(STORAGE_KEY);
            if (savedSessions) {
                const parsedSessions = JSON.parse(savedSessions);
                if (parsedSessions.length > 0) {
                    setSessions(parsedSessions);
                    // Activate the most recent session
                    setActiveSessionId(parsedSessions[0].id);
                }
            }
        }
        catch (error) {
            console.error("Failed to load chat sessions from localStorage", error);
        }
    }, []);
    // Save sessions to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
        }
        catch (error) {
            console.error("Failed to save chat sessions to localStorage", error);
        }
    }, [sessions]);
    const createSession = useCallback(() => {
        const newSession = {
            id: generateUUID(),
            title: '新的对话',
            messages: [],
            createdAt: Date.now(),
        };
        setSessions(prev => [newSession, ...prev]);
        setActiveSessionId(newSession.id);
        return newSession;
    }, []);
    const createSessionForHistory = useCallback((historyId, chartData) => {
        const newSession = {
            id: generateUUID(),
            title: chartData?.person1Name || '新的对话',
            messages: [],
            createdAt: Date.now(),
            relatedHistoryId: historyId,
            relatedChart: chartData,
        };
        setSessions(prev => [newSession, ...prev]);
        setActiveSessionId(newSession.id);
        return newSession;
    }, []);
    const switchToSessionForHistory = useCallback((historyId) => {
        const existingSession = sessions.find(s => s.relatedHistoryId === historyId);
        if (existingSession) {
            setActiveSessionId(existingSession.id);
        }
        else {
            console.log(`No session found for history ID ${historyId}, creating new one`);
        }
    }, [sessions]);
    const getSessionForHistory = useCallback((historyId) => {
        return sessions.find(s => s.relatedHistoryId === historyId) ?? null;
    }, [sessions]);
    const switchSession = useCallback((sessionId) => {
        const sessionExists = sessions.some(s => s.id === sessionId);
        if (sessionExists) {
            setActiveSessionId(sessionId);
        }
        else {
            console.error(`Session with id ${sessionId} not found.`);
        }
    }, [sessions]);
    const deleteSession = useCallback((sessionId) => {
        setSessions(prev => {
            const newSessions = prev.filter(s => s.id !== sessionId);
            if (activeSessionId === sessionId) {
                setActiveSessionId(newSessions.length > 0 ? newSessions[0].id : null);
            }
            return newSessions;
        });
    }, [activeSessionId]);
    const clearAllSessions = useCallback(() => {
        setSessions([]);
        setActiveSessionId(null);
        // The useEffect for localStorage will handle the actual clearing.
    }, []);
    const updateSessionMessages = useCallback((sessionId, newMessages) => {
        setSessions(prev => prev.map(session => session.id === sessionId
            ? { ...session, messages: typeof newMessages === 'function' ? newMessages(session.messages) : newMessages }
            : session));
    }, []);
    const setMessages = useCallback((messages) => {
        if (activeSessionId) {
            updateSessionMessages(activeSessionId, messages);
        }
    }, [activeSessionId, updateSessionMessages]);
    const addMessage = useCallback((message) => {
        if (activeSessionId) {
            updateSessionMessages(activeSessionId, prev => [...prev, message]);
        }
    }, [activeSessionId, updateSessionMessages]);
    const updateLastMessage = useCallback((content) => {
        if (activeSessionId) {
            updateSessionMessages(activeSessionId, prev => {
                if (prev.length === 0 || prev[prev.length - 1].role !== 'assistant') {
                    return prev;
                }
                const newMessages = [...prev];
                newMessages[newMessages.length - 1] = { ...newMessages[newMessages.length - 1], content };
                return newMessages;
            });
        }
    }, [activeSessionId, updateSessionMessages]);
    const clearMessages = useCallback(() => {
        if (activeSessionId) {
            updateSessionMessages(activeSessionId, []);
        }
    }, [activeSessionId, updateSessionMessages]);
    const updateSessionTitle = useCallback((sessionId, title) => {
        setSessions(prev => prev.map(session => session.id === sessionId ? { ...session, title } : session));
    }, []);
    const activeSession = useMemo(() => {
        return sessions.find(s => s.id === activeSessionId) ?? null;
    }, [sessions, activeSessionId]);
    const contextValue = {
        sessions,
        activeSessionId,
        activeSession,
        isLoading,
        isGeneratingInBackground,
        createSession,
        createSessionForHistory,
        switchSession,
        switchToSessionForHistory,
        deleteSession,
        setMessages,
        addMessage,
        updateLastMessage,
        updateSessionTitle,
        clearMessages,
        clearAllSessions,
        getSessionForHistory,
        setIsLoading,
        setIsGeneratingInBackground,
    };
    return (React.createElement(AIChatContext.Provider, { value: contextValue }, children));
};
// --- Custom Hook ---
export const useAIChat = () => {
    const context = useContext(AIChatContext);
    if (context === undefined) {
        throw new Error('useAIChat must be used within an AIChatProvider');
    }
    return context;
};
